// Import Swiper styles
import NiceModal from '@ebay/nice-modal-react';
import { css } from '@emotion/react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Button from 'components/field/Button';
import { ImageSliderModal } from 'components/field/ImageViewer';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import SwiperFooter from 'components/global/SwiperFooter';
import Typography from 'components/global/Typography';

import { faCheck } from '@fortawesome/pro-regular-svg-icons';

import * as S from './ModuleDetail.styled';

const LinkViewer = ({ children }) => {
	const { t } = useTranslation();
	return (
		<a href={children} rel="noopener noreferrer" target="_blank">
			{t(children)}
		</a>
	);
};

const ModuleHero = () => {
	const { selectedModule } = useSelector((state) => state.otoPackageList);
	const { t } = useTranslation();
	const [swiperRef, setSwiperRef] = useState(null);

	if (t(`${selectedModule?.slug}.hero.title`) === `${selectedModule?.slug}.hero.title`) {
		return null;
	}

	const supports = [
		{
			name: 'knowledgeBase',
			action: () => {
				window.open(
					'https://help.tryoto.com/en/support/solutions/150000215953',
					'_blank',
					'noopener,noreferrer'
				);
			}
		},
		{
			name: 'chatWithSupport',
			action: async () => {
				await window.voiceflow?.chat.open();
				await window.voiceflow?.chat.show();
			}
		},
		{
			name: 'scheduleMeeting',
			action: () => {
				window.open(
					'https://meetings.hubspot.com/oto-team/oto-call-request-en',
					'_blank',
					'noopener,noreferrer'
				);
			}
		}
	];

	const images = [
		t(`${selectedModule?.slug}.hero.image1`),
		t(`${selectedModule?.slug}.hero.image2`),
		t(`${selectedModule?.slug}.hero.image3`)
	];

	const hasImage = (url) => {
		return url.includes('https://') || url.includes('http://');
	};
	return (
		<>
			<S.Hero>
				<S.HeroLeft>
					<S.HeroContent>
						<Typography variant="h2" fontWeight="800" noWrap>
							{`${selectedModule?.slug}.hero.title`}
						</Typography>
						<Typography
							variant="p2"
							fontWeight="400"
							color="var(--content-3)"
							noWrap
							css={css`
								line-height: 2.6rem;
							`}>
							<Trans
								i18nKey={`${selectedModule?.slug}.hero.description`}
								components={{
									linkViewer: <LinkViewer />
								}}
							/>
						</Typography>
					</S.HeroContent>
					<S.ImageGallery>
						<Image
							url={t(`${selectedModule?.slug}.hero.image.main`)}
							width="100%"
							height="100%"
							onClick={() =>
								NiceModal.show(ImageSliderModal, {
									imageUrls: [t(`${selectedModule?.slug}.hero.image.main`), ...images]
								})
							}
						/>
						<S.GallerySidebar>
							{images.map(
								(item) =>
									hasImage(item) && (
										<Image
											key={item}
											url={item}
											width="100%"
											height="100%"
											onClick={() =>
												NiceModal.show(ImageSliderModal, {
													imageUrls: [
														item,
														...images.filter((img) => img !== item),
														t(`${selectedModule?.slug}.hero.image.main`)
													]
												})
											}
										/>
									)
							)}
						</S.GallerySidebar>
					</S.ImageGallery>
					<S.MobileGallery>
						<Swiper
							modules={[Autoplay]}
							spaceBetween={24}
							slidesPerView={1}
							autoplay={{
								delay: 3000,
								disableOnInteraction: false
							}}
							onSwiper={setSwiperRef}>
							<SwiperSlide>
								<Image
									url={t(`${selectedModule?.slug}.hero.image.main`)}
									width="100%"
									height="auto"
									onClick={() =>
										NiceModal.show(ImageSliderModal, {
											imageUrls: [t(`${selectedModule?.slug}.hero.image.main`), ...images]
										})
									}
								/>
							</SwiperSlide>
							{images.map((item) => (
								<SwiperSlide key={item}>
									<Image
										url={item}
										width="100%"
										height="auto"
										onClick={() =>
											NiceModal.show(ImageSliderModal, {
												imageUrls: [
													item,
													...images.filter((img) => img !== item),
													t(`${selectedModule?.slug}.hero.image.main`)
												]
											})
										}
									/>
								</SwiperSlide>
							))}
							{images.length > 1 && swiperRef && (
								<SwiperFooter cssPosition="relative" swiperRef={swiperRef} />
							)}
						</Swiper>
					</S.MobileGallery>
				</S.HeroLeft>
				<S.HeroSupport>
					<S.SupportItem>
						<Typography variant="p3" fontWeight="700">
							availableFor
						</Typography>
						{selectedModule?.packageNames?.map((item) => (
							<S.AvailablePlan key={item}>
								<Icon icon={faCheck} color="var(--green)" />
								<Typography variant="p3" fontWeight="400" color="var(--content-3)">
									{item}
								</Typography>
							</S.AvailablePlan>
						))}
					</S.SupportItem>
					<S.SupportItem>
						<Typography variant="p3" fontWeight="700">
							support
						</Typography>
						{supports.map((item) => (
							<Button key={item.name} label={item.name} onClick={item.action} variant="transparent" />
						))}
					</S.SupportItem>
				</S.HeroSupport>
			</S.Hero>
			<S.Line />
		</>
	);
};

export default ModuleHero;
